import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Class-based React Error Boundary.
 *
 * Catches rendering errors from any child component so a single bad
 * section (e.g. malformed TipTap content) never blanks the entire screen.
 * Shows a recovery UI with the error message + 2 actions:
 *   - Try again: forces a re-render by bumping `key`.
 *   - Go to dashboard: navigates the user out of the broken context.
 *
 * Logs to console only — we deliberately avoid posting to Sentry/etc.
 * to keep the dependency surface tight; doctors can screenshot the
 * shown error and send it to us.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the full stack to the browser console so it's available in
    // user-shared HAR / screenshot reports.
    console.error('[ErrorBoundary] Caught render error:', error, info);
    this.setState({ info });
  }

  handleRetry = () => {
    this.setState((s) => ({ hasError: false, error: null, info: null, retryKey: s.retryKey + 1 }));
  };

  handleHome = () => {
    window.location.assign(this.props.homeUrl || '/dashboard');
  };

  render() {
    if (this.state.hasError) {
      const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
      const stack = (this.state.info && this.state.info.componentStack) || '';
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6 bg-slate-50" data-testid="error-boundary">
          <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-red-100 overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-6 py-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>
                  Something went wrong while rendering this screen
                </h2>
                <p className="text-[11px] text-white/80">Your data is safe — only the display crashed.</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-1">Error</p>
                <p className="text-sm text-red-900 font-mono break-words" data-testid="error-boundary-message">
                  {msg}
                </p>
              </div>
              {stack && (
                <details className="text-[10px] text-slate-500">
                  <summary className="cursor-pointer text-slate-600 font-semibold hover:text-slate-800">
                    Show technical details
                  </summary>
                  <pre className="mt-2 p-2 bg-slate-100 rounded text-[10px] overflow-x-auto max-h-40 whitespace-pre-wrap">
                    {stack}
                  </pre>
                </details>
              )}
              <p className="text-xs text-slate-600">
                Try again, or return to the dashboard. If this keeps happening, please screenshot this
                screen and contact support.
              </p>
              <div className="flex gap-2">
                <Button onClick={this.handleRetry} className="gap-1.5 bg-blue-700 hover:bg-blue-800 flex-1" data-testid="error-boundary-retry">
                  <RefreshCw className="h-4 w-4" /> Try again
                </Button>
                <Button variant="outline" onClick={this.handleHome} className="gap-1.5" data-testid="error-boundary-home">
                  <Home className="h-4 w-4" /> Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    // `retryKey` forces the entire subtree to remount on "Try again", so any
    // hooks holding bad state are reset cleanly.
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
